import React, { useState } from 'react';
import {
  Typography,
  Container,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  Button,
  Skeleton,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  ListAlt as ListAltIcon,
  History as HistoryIcon,
  Event as EventIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { BottomNavigation } from '../layout/BottomNavigation';
import { TopBar } from '../layout/TopBar';
import { useTodoList, TodoFilter } from '../hooks/useTodoList';
import { AddTodoDialog } from '../components/todos/AddTodoDialog';

export const TodoListPage: React.FC = () => {
  const { theme } = useTheme();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Garantir que a data inicial seja hoje (sem hora) - Memoized to prevent re-renders
  const today = React.useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const { todosForDate, loading: todosLoading, addTodo, toggleTodo, deleteTodo } = useTodoList();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewFilter, setViewFilter] = useState<TodoFilter>('month');
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; todoId: string | null; title: string }>({ open: false, todoId: null, title: '' });
  const [deleting, setDeleting] = useState(false);

  const allTodos = todosForDate(viewFilter, true);

  const formatTodoDate = (date: Date) =>
    date.toLocaleString('pt-BR', { day: '2-digit', month: 'short' }).toUpperCase();

  const isSameDay = (date: Date) => {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized.getTime() === today.getTime();
  };

  const isOverdue = (date: Date) => {
    const todoDate = new Date(date);
    todoDate.setHours(0, 0, 0, 0);
    return todoDate < today;
  };

  // Formatar data para exibição
  const formatDate = (date: Date) => {
    return {
      day: date.getDate().toString().padStart(2, '0'),
      month: date.toLocaleString('pt-BR', { month: 'short' }).toUpperCase(),
    };
  };

  const displayDate = formatDate(today);

  const getFilterLabel = () => {
    switch (viewFilter) {
      case 'today':
        return 'TAREFAS - HOJE';
      case 'week':
        return 'TAREFAS - SEMANA';
      case 'month':
        return 'TAREFAS - ESTE MÊS';
      default:
        return 'TAREFAS';
    }
  };

  // Loading state
  if (authLoading) {
    return (
      <>
        <TopBar
          title="Tarefas"
          icon={<ListAltIcon sx={{ fontSize: '0.875rem' }} />}
          showBackButton={true}
          onBackClick={() => navigate(-1)}
        />
        <Box sx={{
          backgroundColor: theme.colors.background,
          minHeight: '100vh',
          paddingBottom: '120px',
          paddingTop: '70px',
        } as const}>
          <Container component="main" sx={{ flexGrow: 1, padding: '16px' }}>
            <Skeleton variant="rectangular" height={100} sx={{ borderRadius: theme.radii.medium, mb: 2 }} />
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: theme.radii.medium, mb: 2 }} />
            <Skeleton variant="rectangular" height={150} sx={{ borderRadius: theme.radii.medium }} />
          </Container>
        </Box>
      </>
    );
  }

  // Require authentication
  if (!user) {
    navigate('/login');
    return null;
  }

  const handleAddTodo = React.useCallback(async (title: string, date: Date) => {
    await addTodo(title, date);
  }, [addTodo]);

  const handleToggleTodo = React.useCallback(async (todoId: string, completed: boolean) => {
    await toggleTodo(todoId, completed);
  }, [toggleTodo]);

  const handleConfirmDelete = async () => {
    if (!deleteDialog.todoId) return;
    try {
      setDeleting(true);
      await deleteTodo(deleteDialog.todoId);
      setDeleteDialog({ open: false, todoId: null, title: '' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <TopBar
        title="Tarefas"
        icon={<ListAltIcon sx={{ fontSize: '0.875rem' }} />}
        showBackButton={true}
        showProfileButton={true}
        showSearchButton={true}
        onBackClick={() => navigate(-1)}
        onSearchClick={() => {
          const searchInput = document.querySelector('input[placeholder*="Buscar"]') as HTMLInputElement;
          if (searchInput) {
            searchInput.focus();
            searchInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }}
      />

      <Box sx={{
        backgroundColor: theme.colors.background,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        paddingBottom: '120px',
        paddingTop: '70px',
      }}>
        <Container component="main" sx={{ flexGrow: 1, padding: '16px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Typography variant="h6" component="h1" sx={{ fontWeight: 'bold', color: theme.colors.text.primary }}>
              {getFilterLabel()}
            </Typography>
            <Box sx={{
              backgroundColor: theme.colors.secondary,
              color: theme.colors.primary,
              borderRadius: theme.radii.medium,
              padding: '12px 16px',
              fontWeight: 'bold',
              textAlign: 'center',
              width: 'fit-content',
            }}>
              <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>{displayDate.day}</Typography>
              <Typography variant="body2" component="div">{displayDate.month}</Typography>
            </Box>
          </Box>

          <FormGroup>
            {todosLoading ? (
              [1, 2, 3, 4, 5].map((item) => (
                <Box key={item} sx={{ borderBottom: `1px solid ${theme.colors.border}`, py: 1 }}>
                  <Skeleton variant="text" height={24} width="70%" />
                </Box>
              ))
            ) : allTodos.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography sx={{ color: theme.colors.text.secondary, fontSize: '0.875rem' }}>
                  {viewFilter === 'today' && 'Nenhuma tarefa para hoje'}
                  {viewFilter === 'week' && 'Nenhuma tarefa para esta semana'}
                  {viewFilter === 'month' && 'Nenhuma tarefa para este mês'}
                </Typography>
                <Typography sx={{ color: theme.colors.text.secondary, fontSize: '0.75rem', mt: 1 }}>
                  Adicione suas primeiras tarefas!
                </Typography>
              </Box>
            ) : (
              allTodos.map((todo) => {
                const overdueTask = !todo.completed && isOverdue(todo.date);
                const showDueInfo = !isSameDay(todo.date) || viewFilter !== 'month';

                return (
                  <Box
                    key={todo.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      borderBottom: `1px solid ${theme.colors.border}`,
                      pr: 0.5,
                    }}
                  >
                  <FormControlLabel
                    sx={{ flex: 1, m: 0 }}
                    control={
                      <Checkbox
                        checked={todo.completed}
                        onChange={(e) => handleToggleTodo(todo.id, e.target.checked)}
                        sx={{ '&.Mui-checked': { color: theme.colors.primary } }}
                      />
                    }
                    label={
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                          <Typography sx={{
                            textDecoration: todo.completed ? 'line-through' : 'none',
                            color: overdueTask ? '#d32f2f' : todo.completed ? theme.colors.text.secondary : theme.colors.text.primary,
                            opacity: todo.completed ? 0.6 : 1
                          }}>
                            {todo.title}
                          </Typography>
                          {todo.eventId && todo.eventTitle && (
                            <Chip
                              icon={<EventIcon sx={{ fontSize: '0.7rem !important' }} />}
                              label={todo.eventTitle}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.6rem',
                                fontWeight: 'bold',
                                backgroundColor: theme.colors.secondary + '30',
                                color: theme.colors.primary,
                                '& .MuiChip-icon': {
                                  color: theme.colors.primary,
                                },
                              }}
                            />
                          )}
                        </Box>
                        {showDueInfo && (
                          <Typography sx={{
                            fontSize: '0.7rem',
                            color: overdueTask ? '#d32f2f' : theme.colors.text.secondary,
                            mt: 0.5
                          }}>
                            {overdueTask
                              ? `Atrasada desde ${formatTodoDate(todo.date)}`
                              : `${formatTodoDate(todo.date)}`}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <IconButton
                    size="small"
                    aria-label="Apagar tarefa"
                    onClick={() => setDeleteDialog({ open: true, todoId: todo.id, title: todo.title })}
                    sx={{ color: theme.colors.text.secondary, ml: 1 }}
                  >
                    <DeleteIcon sx={{ fontSize: '1.1rem' }} />
                  </IconButton>
                  </Box>
                );
              })
            )}
          </FormGroup>

          <Box sx={{ textAlign: 'right', mt: 1 }}>
            <Button
              onClick={() => setViewFilter('all')}
              sx={{ color: theme.colors.secondary, fontWeight: 'bold' }}
            >
              Ver tudo
            </Button>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <Button
              onClick={() => setViewFilter('today')}
              sx={{
                backgroundColor: viewFilter === 'today' ? theme.colors.primary : theme.colors.secondary,
                color: viewFilter === 'today' ? 'white' : theme.colors.primary,
                margin: '0 8px',
                padding: '10px 20px',
                fontWeight: 'bold',
                borderRadius: theme.radii.medium,
                '&:hover': {
                  backgroundColor: viewFilter === 'today' ? theme.colors.primary : theme.colors.secondary,
                  opacity: 0.8,
                }
              }}
            >
              Hoje
            </Button>
            <Button
              onClick={() => setViewFilter('week')}
              sx={{
                backgroundColor: viewFilter === 'week' ? theme.colors.primary : theme.colors.secondary,
                color: viewFilter === 'week' ? 'white' : theme.colors.primary,
                margin: '0 8px',
                padding: '10px 20px',
                fontWeight: 'bold',
                borderRadius: theme.radii.medium,
                '&:hover': {
                  backgroundColor: viewFilter === 'week' ? theme.colors.primary : theme.colors.secondary,
                  opacity: 0.8,
                }
              }}
            >
              Semana
            </Button>
            <Button
              onClick={() => setViewFilter('month')}
              sx={{
                backgroundColor: viewFilter === 'month' ? theme.colors.primary : theme.colors.secondary,
                color: viewFilter === 'month' ? 'white' : theme.colors.primary,
                margin: '0 8px',
                padding: '10px 20px',
                fontWeight: 'bold',
                borderRadius: theme.radii.medium,
                '&:hover': {
                  backgroundColor: viewFilter === 'month' ? theme.colors.primary : theme.colors.secondary,
                  opacity: 0.8,
                }
              }}
            >
              Mês
            </Button>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Button
              onClick={() => setDialogOpen(true)}
              sx={{
                backgroundColor: theme.colors.primary,
                color: theme.colors.text.contrast,
                width: '100%',
                maxWidth: '250px',
                padding: '10px',
                margin: '8px 0',
                borderRadius: theme.radii.medium,
                '&:hover': {
                  backgroundColor: theme.colors.primary,
                  opacity: 0.8,
                },
              }}
            >
              Adicionar Tarefa
            </Button>
            <Button
              startIcon={<HistoryIcon />}
              onClick={() => navigate('/todos/history')}
              sx={{
                backgroundColor: theme.colors.primary,
                color: theme.colors.text.contrast,
                width: '100%',
                maxWidth: '250px',
                padding: '10px',
                margin: '8px 0',
                borderRadius: theme.radii.medium,
                '&:hover': {
                  backgroundColor: theme.colors.primary,
                  opacity: 0.8,
                },
              }}
            >
              Histórico de tarefas
            </Button>
          </Box>
        </Container>

        <BottomNavigation />
      </Box>

      <AddTodoDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleAddTodo}
        initialDate={today}
      />

      <Dialog
        open={deleteDialog.open}
        onClose={() => !deleting && setDeleteDialog({ open: false, todoId: null, title: '' })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Apagar tarefa?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja apagar <strong>{deleteDialog.title}</strong>? Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, todoId: null, title: '' })}
            disabled={deleting}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            Apagar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
